<template>
  <div style="display: contents;">
    <!-- Notification Dropdown -->
    <b-nav-item-dropdown class="d-flex align-items-center navbar-action-item notification-dropdown" right no-caret menu-class="notification-dropdown-menu">
      <template #button-content>
        <span class="notification-trigger" v-b-tooltip.hover title="Notifications">
          <i class="ri-notification-3-line"></i>
          <span
            v-if="totalUnreadCount"
            class="notification-badge"
            :class="{ 'notification-badge-pulse': hasAnyDanger }">
            {{ totalUnreadCount > 99 ? '99+' : totalUnreadCount }}
          </span>
        </span>
      </template>

      <div class="notification-panel">
        <div class="notification-header">
          <span>Notifications</span>
          <span v-if="totalUnreadCount" class="notification-count-pill">
            {{ totalUnreadCount }}
          </span>
        </div>

        <div v-if="isAnyNotificationLoading && !totalUnreadCount" class="notification-empty">
          <b-spinner small variant="primary" />
          <p>Loading notifications...</p>
        </div>

        <div v-else-if="!totalUnreadCount" class="notification-empty">
          <i class="ri-check-double-line"></i>
          <p>You're all caught up</p>
        </div>

        <div v-else class="notification-list">
          <!-- Expiration Section -->
          <div v-if="notifications.length" class="notification-section">
            <button
              type="button"
              class="notification-section-header"
              :aria-expanded="sectionExpanded.expiration ? 'true' : 'false'"
              @click.stop.prevent="toggleSection('expiration')">
              <span class="notification-section-title">Subscription Expiration</span>
              <span class="notification-section-meta">
                <span class="notification-section-count">{{ unreadNotificationsCount }}</span>
                <span
                  class="notification-section-arrow"
                  :class="{ collapsed: !sectionExpanded.expiration }"
                  aria-hidden="true">&#9662;</span>
              </span>
            </button>
            <div v-show="sectionExpanded.expiration">
              <b-dropdown-item
                v-for="notification in notifications"
                :key="notification.id"
                class="notification-item notification-item-unread"
                :class="`notification-${notification.severity}`"
                @click="handleNotifClick(notification)">
                <div class="notification-status" aria-hidden="true"></div>
                <div class="notification-content">
                  <div v-if="notification.shipName" class="notification-ship">{{ notification.shipName }}</div>
                  <div class="notification-title">{{ notification.title }}</div>
                  <div class="notification-message">{{ notification.message }}</div>
                  <div class="notification-time">Expired at: {{ formatNotifDate(notification.time) }}</div>
                </div>
              </b-dropdown-item>
            </div>
          </div>

          <!-- Event Section -->
          <div v-if="canRead('log_event_event') && eventNotifications.length" class="notification-section">
            <button
              type="button"
              class="notification-section-header"
              :aria-expanded="sectionExpanded.events ? 'true' : 'false'"
              @click.stop.prevent="toggleSection('events')">
              <span class="notification-section-title">Events</span>
              <span class="notification-section-meta">
                <span class="notification-section-count">{{ eventUnreadCount }}</span>
                <span
                  class="notification-section-arrow"
                  :class="{ collapsed: !sectionExpanded.events }"
                  aria-hidden="true">&#9662;</span>
              </span>
            </button>
            <div v-show="sectionExpanded.events">
              <b-dropdown-item
                v-for="notification in eventNotifications"
                :key="notification.id"
                class="notification-item notification-item-unread"
                :class="`notification-${notification.severity}`"
                @click="openEventDetail(notification)">
                <div class="notification-status" aria-hidden="true"></div>
                <div class="notification-content">
                  <div v-if="notification.shipName" class="notification-ship">{{ notification.shipName }}</div>
                  <div class="notification-title">
                    {{ notification.title }}
                    <b-badge
                      :variant="notification.status === 'Active' ? 'danger' : 'secondary'"
                      class="ml-1 align-middle event-notif-badge">
                      {{ notification.status }}
                    </b-badge>
                  </div>
                  <div class="notification-message">
                    Duration {{ formatEventDuration(notification.duration) }}
                  </div>
                  <div class="notification-time">At: {{ formatEventTime(notification.time) }}</div>
                </div>
              </b-dropdown-item>
            </div>
          </div>
        </div>

        <div v-if="totalUnreadCount" class="notification-footer-group">
          <button
            v-if="notifications.length"
            type="button"
            class="notification-footer notification-footer-split"
            @click.stop="handleViewAll">
            Expired Assets
          </button>
          <button
            v-if="eventNotifications.length"
            type="button"
            class="notification-footer notification-footer-split"
            @click.stop="goToEventLog">
            Event Log
          </button>
        </div>
      </div>
    </b-nav-item-dropdown>
    <!--/ Notification Dropdown -->

    <!-- Event Detail Modal -->
    <b-modal size="lg" hide-footer no-close-on-esc no-close-on-backdrop v-model="isOpenEventDetail">
      <template #modal-header>
        <div class="d-flex align-items-center flex-wrap">
          <h5 class="modal-title mr-2 mb-0">{{ selectedEvent ? selectedEvent.title : 'Event Detail' }}</h5>
          <b-badge
            v-if="selectedEvent"
            :variant="selectedEvent.status === 'Active' ? 'danger' : 'secondary'"
            class="mr-2">
            {{ selectedEvent.status }}
          </b-badge>
          <b-badge
            v-if="selectedEvent"
            :variant="selectedEvent.priority === 'High' ? 'warning' : 'info'">
            {{ selectedEvent.priority }} priority
          </b-badge>
        </div>
        <b-button variant="outline-danger" size="sm" @click="closeEventDetail">
          <i class="ri-close-fill"></i>
        </b-button>
      </template>

      <b-container class="my-2" v-if="selectedEvent">
        <b-row class="mb-3 text-muted small">
          <b-col cols="12" md="6"><strong>Time:</strong> {{ formatEventTime(selectedEvent.time) }}</b-col>
          <b-col cols="12" md="6"><strong>Duration:</strong> {{ formatEventDuration(selectedEvent.duration) }}</b-col>
          <b-col cols="12" md="6" class="mt-1"><strong>Ship:</strong> {{ selectedEvent.shipName || '-' }}</b-col>
          <b-col cols="12" md="6" class="mt-1"><strong>Device ID:</strong> {{ selectedEvent.deviceId || '-' }}</b-col>
        </b-row>

        <h6 class="mt-3 mb-2">Data Changes</h6>
        <b-table
          v-if="parsedEventDetail.dataRows.length"
          :items="parsedEventDetail.dataRows"
          :fields="eventDataFields"
          striped hover small responsive>
          <template #cell(device)="data">
            <strong>{{ data.item.device }}</strong>
          </template>
          <template #cell(type)="data">
            <span class="text-primary">{{ data.item.type }}</span>
          </template>
        </b-table>
        <div v-else class="text-muted small mb-2">No data changes recorded.</div>

        <h6 class="mt-4 mb-2">Thresholds</h6>
        <div v-if="Object.keys(parsedEventDetail.thresholds).length">
          <b-card
            v-for="(item, name) in parsedEventDetail.thresholds"
            :key="name"
            class="mb-2">
            <template #header>
              <div class="d-flex justify-content-between align-items-center">
                <strong>{{ name }}</strong>
                <b-badge v-if="item.conditions_operator" variant="info">
                  {{ item.conditions_operator }}
                </b-badge>
              </div>
            </template>
            <b-table
              small
              :items="formatThresholdRows(item.data)"
              :fields="thresholdFields"
              responsive="sm" bordered head-variant="light" class="mb-0" />
          </b-card>
        </div>
        <div v-else class="text-muted small">No threshold information.</div>
      </b-container>
    </b-modal>
    <!--/ Event Detail Modal -->
  </div>
</template>

<script>
export default {
  name: 'NavbarNotifications',
  data() {
    return {
      notificationPollMs: 5 * 60 * 1000,
      eventNotificationPollMs: 60 * 1000,
      isOpenEventDetail: false,
      selectedEvent: null,
      sectionExpanded: {
        expiration: true,
        events: true,
      },
      eventDataFields: [
        { key: 'device', label: 'Device' },
        { key: 'type', label: 'Type' },
        { key: 'dataName', label: 'Data Name' },
        { key: 'oldValue', label: 'Old Value' },
        { key: 'newValue', label: 'New Value' },
      ],
      thresholdFields: [
        { key: 'parameter', label: 'Parameter' },
        { key: 'operator', label: 'Operator' },
        { key: 'value', label: 'Value' },
      ],
    };
  },
  computed: {
    notifications() {
      return this.$store.getters['notification/notifications'];
    },
    unreadNotificationsCount() {
      return this.$store.getters['notification/unreadCount'];
    },
    isNotificationLoading() {
      return this.$store.getters['notification/loading'];
    },
    eventNotifications() {
      return this.$store.getters['eventNotification/notifications'];
    },
    eventUnreadCount() {
      return this.$store.getters['eventNotification/unreadCount'];
    },
    isEventNotificationLoading() {
      return this.$store.getters['eventNotification/loading'];
    },
    totalUnreadCount() {
      return this.unreadNotificationsCount + this.eventUnreadCount;
    },
    isAnyNotificationLoading() {
      return this.isNotificationLoading || this.isEventNotificationLoading;
    },
    hasAnyDanger() {
      return this.notifications.some((n) => n.severity === 'danger')
        || this.eventNotifications.some((n) => n.severity === 'danger');
    },
    parsedEventDetail() {
      if (!this.selectedEvent) return { dataRows: [], thresholds: {} };
      const raw = this.selectedEvent.raw || {};
      const oldData = this.safeJsonParse(raw.evntOldData) || {};
      const newData = this.safeJsonParse(raw.evntNewData) || {};
      const thresholds = this.safeJsonParse(raw.evntThreshold) || {};

      const dataRows = [];
      const devices = new Set([
        ...Object.keys(oldData),
        ...Object.keys(newData),
      ]);
      devices.forEach((device) => {
        const types = new Set([
          ...Object.keys(oldData[device] || {}),
          ...Object.keys(newData[device] || {}),
        ]);
        types.forEach((type) => {
          const dataNames = new Set([
            ...Object.keys((oldData[device] || {})[type] || {}),
            ...Object.keys((newData[device] || {})[type] || {}),
          ]);
          dataNames.forEach((dataName) => {
            dataRows.push({
              device,
              type,
              dataName,
              oldValue: (oldData[device] || {})[type]?.[dataName] ?? '-',
              newValue: (newData[device] || {})[type]?.[dataName] ?? '-',
            });
          });
        });
      });

      return { dataRows, thresholds };
    },
  },
  mounted() {
    this.$store.dispatch('notification/fetch');
    this._notifPoll = setInterval(() => {
      this.$store.dispatch('notification/fetch');
    }, this.notificationPollMs);

    if (this.canRead('log_event_event')) {
      this.$store.dispatch('eventNotification/fetch');
      this._eventNotifPoll = setInterval(() => {
        this.$store.dispatch('eventNotification/fetch');
      }, this.eventNotificationPollMs);
    }
  },
  beforeDestroy() {
    if (this._notifPoll) clearInterval(this._notifPoll);
    if (this._eventNotifPoll) clearInterval(this._eventNotifPoll);
  },
  methods: {
    formatNotifDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },
    handleNotifClick(notification) {
      if (notification.massId && this.$route.name !== 'Asset') {
        this.$router.push({
          name: 'Asset',
          query: { highlight: notification.massId },
        });
      }
    },
    handleViewAll() {
      if (this.$route.name === 'Asset' && this.$route.query.subscriptionFilter === 'expired') return;
      this.$router.push({
        name: 'Asset',
        query: { subscriptionFilter: 'expired' },
      });
    },
    openEventDetail(notification) {
      this.selectedEvent = notification;
      this.isOpenEventDetail = true;
    },
    toggleSection(name) {
      this.sectionExpanded = {
        ...this.sectionExpanded,
        [name]: !this.sectionExpanded[name],
      };
    },
    closeEventDetail() {
      this.isOpenEventDetail = false;
      this.selectedEvent = null;
    },
    formatEventTime(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return '-';
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
    formatEventDuration(seconds) {
      const s = Number(seconds) || 0;
      const pad = (n) => String(n).padStart(2, '0');
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      return `${pad(h)}:${pad(m)}:${pad(sec)}`;
    },
    formatThresholdRows(data) {
      if (!data || typeof data !== 'object') return [];
      return Object.keys(data).map((key) => ({
        parameter: key,
        operator: data[key]?.operator ?? '-',
        value: data[key]?.value ?? '-',
      }));
    },
    safeJsonParse(str) {
      if (!str) return null;
      if (typeof str !== 'string') return str;
      try {
        return JSON.parse(str);
      } catch (e) {
        return null;
      }
    },
    goToEventLog() {
      if (this.$route.name !== 'EventLog') {
        this.$router.push({ name: 'EventLog' });
      }
    },
  },
};
</script>
